import React from 'react';
import { Tooltip } from 'antd';
import { MedicineBoxOutlined } from '@ant-design/icons';
import type { DrugRouteSummary } from '../types';

interface RouteGlyphProps {
  code: DrugRouteSummary['code'] | 'continuous_infusion';
}

const RouteGlyph: React.FC<RouteGlyphProps> = ({ code }) => {
  if (code === 'iv_infusion') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M7 3h10v8a4 4 0 0 1-4 4h-2a4 4 0 0 1-4-4V3Zm0 5h10M12 15v5" />
        <path d="M12 20c-1.2 1.3-1.2 2.2 0 2.2s1.2-.9 0-2.2Z" />
      </svg>
    );
  }

  if (code === 'continuous_infusion') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="3.5" y="5" width="10" height="14" rx="2" />
        <path d="M6.5 9h4M6.5 12h4M13.5 9h2.5c2.5 0 4.5 2 4.5 4.5V16" />
        <circle cx="20.5" cy="18.5" r="1.5" />
      </svg>
    );
  }

  if (code === 'iv_injection') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="m14.5 4.5 5 5M16 3l5 5M8.5 10.5l5 5m-7-7 9 9m-10-7-2.5 2.5a2 2 0 0 0 0 3L5 17l2 2 1 2 3-3m-6-1-2 2m13-9 3-3" />
      </svg>
    );
  }

  if (code === 'oral') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M8.3 5.3a4.24 4.24 0 0 1 6 0l4.4 4.4a4.24 4.24 0 0 1-6 6l-4.4-4.4a4.24 4.24 0 0 1 0-6ZM9 12l6-6" />
      </svg>
    );
  }

  return <MedicineBoxOutlined aria-hidden="true" />;
};

interface ClinicalRouteIconProps {
  route: DrugRouteSummary;
  glyphCode?: RouteGlyphProps['code'];
  showLabel?: boolean;
  compact?: boolean;
}

const ClinicalRouteIcon: React.FC<ClinicalRouteIconProps> = ({
  route,
  glyphCode,
  showLabel = false,
  compact = false,
}) => {
  const content = (
    <span
      className={`clinical-route${compact ? ' clinical-route--compact' : ''}`}
      aria-label={route.label}
      role="img"
      tabIndex={showLabel ? undefined : 0}
    >
      <span className="clinical-route__glyph">
        <RouteGlyph code={glyphCode || route.code} />
      </span>
      {showLabel ? <span className="clinical-route__label">{route.label}</span> : null}
    </span>
  );

  return showLabel ? content : <Tooltip title={route.label}>{content}</Tooltip>;
};

export default ClinicalRouteIcon;
